import type { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import { AxiosError } from "axios"

export interface IParsedAxiosError {
  statusCode: number
  message: string
  error: string
}

const isFetchBaseQueryError = (err: unknown): err is FetchBaseQueryError =>
  typeof err === "object" && err !== null && "status" in err

export const parseAxiosError = (err: unknown): IParsedAxiosError => {
  // RTK Query FetchBaseQueryError — new branch. Pulls data.message / data.error
  // so call sites in RTK Query code paths no longer need a private `parseRtkError`.
  if (isFetchBaseQueryError(err)) {
    const body =
      typeof err.data === "object" && err.data !== null
        ? (err.data as {
            message?: string
            error?: string
            statusCode?: number
          })
        : null
    return {
      statusCode:
        body?.statusCode ?? (typeof err.status === "number" ? err.status : 500),
      error:
        body?.error ??
        (typeof err.status === "string" ? err.status : "RTK_QUERY_ERROR"),
      message: body?.message ?? ""
    }
  }

  // Existing axios branch — behavior preserved. The only surface change is
  // the renamed return type (AppError → IParsedAxiosError).
  const source = (err as { error?: unknown } | undefined)?.error ?? err
  const e = source as AxiosError<IParsedAxiosError>
  const body = e?.response?.data

  return {
    statusCode: body?.statusCode || e?.status || 500,
    error: body?.error || e?.code || "INITIAL_SERVER_ERROR",
    message: body?.message || e?.message || "Unknown error occurred"
  }
}
